import { normalizeExplorerPath } from '@/lib/explorer-tree-focus'

/** Minimal tab shape for open-echo matching (paths + document ids). */
export type OpenTabMatchInput = {
  id: string
  filePath?: string | null
  /** Explorer folder tabs keep the open path here, not on filePath. */
  explorerData?: { folderPath?: string | null } | null
}

export type OpenTabIndex = {
  paths: Set<string>
  ids: Set<string>
}

const DOC_PREFIX = 'doc-'
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function stripDocPrefix(id: string): string {
  return id.startsWith(DOC_PREFIX) ? id.slice(DOC_PREFIX.length) : id
}

function addId(ids: Set<string>, raw: string | null | undefined): void {
  if (!raw) return
  const trimmed = String(raw).trim()
  if (!trimmed) return
  ids.add(trimmed)
  const bare = stripDocPrefix(trimmed)
  if (bare !== trimmed) ids.add(bare)
  if (UUID_RE.test(bare)) {
    ids.add(bare)
    ids.add(`${DOC_PREFIX}${bare}`)
  }
}

function addPath(paths: Set<string>, raw: string | null | undefined): void {
  if (!raw) return
  const n = normalizeExplorerPath(raw)
  if (!n) return
  paths.add(n)
}

/**
 * Build a lookup of open tab paths and document ids for sidebar open-echo.
 * Paths come from `filePath`, explorer folder tabs, or path-shaped `id`.
 * Ids include bare UUID and `doc-` forms.
 */
export function buildOpenTabIndex(openTabs: OpenTabMatchInput[]): OpenTabIndex {
  const paths = new Set<string>()
  const ids = new Set<string>()

  for (const tab of openTabs) {
    if (tab.filePath) {
      addPath(paths, tab.filePath)
    }
    if (tab.explorerData?.folderPath) {
      addPath(paths, tab.explorerData.folderPath)
    }
    if (typeof tab.id === 'string' && tab.id.includes('/')) {
      addPath(paths, tab.id)
    }
    addId(ids, tab.id)
  }

  return { paths, ids }
}

/** Paths only - drop-in for existing `openTabPaths` memos / browse focus. */
export function openTabPathsFromIndex(index: OpenTabIndex): string[] {
  return [...index.paths]
}

export function isOpenInTabs(
  index: OpenTabIndex,
  candidate: { path?: string | null; documentId?: string | null },
): boolean {
  if (candidate.documentId) {
    const bare = stripDocPrefix(String(candidate.documentId).trim())
    if (
      index.ids.has(candidate.documentId) ||
      index.ids.has(bare) ||
      index.ids.has(`${DOC_PREFIX}${bare}`)
    ) {
      return true
    }
  }
  if (candidate.path) {
    const n = normalizeExplorerPath(candidate.path)
    if (n && index.paths.has(n)) return true
  }
  return false
}
